/**
 * SelecteurFuseau — choisir un fuseau horaire. La version pure de `TimezoneSelect` du cockpit
 * (`apps/cockpit/src/shared/ui/TimezoneSelect.tsx`, quatre appelants), dessinée par le `Select` de
 * la librairie : l'appareil en tête (« Détecté »), les marchés servis par région, la valeur
 * courante ajoutée si elle manque, l'avance sur UTC dans chaque libellé (« Paris · UTC+1 »).
 *
 *   <SelecteurFuseau etiquette="Fuseau horaire" value={tz} onChange={setTz} />
 *
 * Les décalages se lisent à `maintenant` (défaut : l'instant présent) : « Paris · UTC+2 » l'été.
 */
import type { ReactNode } from "react";

import type { TailleChamp } from "../Champ";
import { Select } from "../Select";
import { Globe } from "../icons";
import { formatDecalage } from "./format";
import { groupesDeFuseaux, type OptionFuseau } from "./fuseaux";

export interface SelecteurFuseauProps {
  /** L'identifiant IANA (« Europe/Paris »). */
  value: string;
  onChange: (fuseau: string) => void;
  etiquette?: ReactNode;
  aide?: ReactNode;
  erreur?: ReactNode;
  /** La liste proposée ; défaut `FUSEAUX` (noms de villes en français). */
  fuseaux?: readonly OptionFuseau[];
  /** Le fuseau de l'appareil, proposé en tête ; `null` pour ne pas le proposer. Défaut : celui du navigateur. */
  appareil?: string | null;
  /** L'instant auquel on lit les décalages. */
  maintenant?: number;
  taille?: TailleChamp;
  pilule?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  "aria-label"?: string;
  textes?: {
    /** Défaut « Détecté ». */
    detecte?: string;
    /** Défaut « Autre ». */
    autre?: string;
  };
  className?: string;
}

export function SelecteurFuseau({
  value,
  onChange,
  etiquette,
  aide,
  erreur,
  fuseaux,
  appareil,
  maintenant,
  taille,
  pilule,
  disabled,
  name,
  id,
  "aria-label": ariaLabel,
  textes,
  className,
}: SelecteurFuseauProps) {
  const groupes = groupesDeFuseaux(value, { fuseaux, appareil, detecte: textes?.detecte, autre: textes?.autre });
  const a = maintenant ?? Date.now();
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      etiquette={etiquette}
      aide={aide}
      erreur={erreur}
      icone={<Globe aria-hidden />}
      taille={taille}
      pilule={pilule}
      disabled={disabled}
      name={name}
      id={id}
      aria-label={ariaLabel}
      className={className}
    >
      {groupes.map((g) => (
        <optgroup key={g.region} label={g.region}>
          {g.fuseaux.map((f) => (
            <option key={f.id} value={f.id}>
              {`${f.ville} · ${formatDecalage(f.id, a)}`}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  );
}
